import { gql, useMutation, useQuery } from "@apollo/client";
import usePrevious from "use-previous";

import { useAuthState } from "../contexts/Auth/context";

const FAVORITE_PACKAGE_IDS_QUERY = gql`
  query getFavoritePackageIds {
    favoritePackageIds
  }
`;

const ADD_FAVORITE_PACKAGE = gql`
  mutation AddFavoritePackage($input: AddFavoritePackageInput!) {
    AddFavoritePackage(input: $input) {
      __typename
    }
  }
`;

const REMOVE_FAVORITE_PACKAGE = gql`
  mutation RemoveFavoritePackage($input: RemoveFavoritePackageInput!) {
    RemoveFavoritePackage(input: $input) {
      __typename
    }
  }
`;

export function useAddFavoritePackage(packageId: string) {
  return useMutation(ADD_FAVORITE_PACKAGE, {
    variables: {
      input: { packageId },
    },
    optimisticResponse: {
      __typename: "Mutation",
      RemoveFavoritePackage: {
        __typename: "RemoveFavoritePackagePayload",
      },
    },
    update(cache) {
      const query = FAVORITE_PACKAGE_IDS_QUERY;
      const data = cache.readQuery<{ favoritePackageIds: string[] }>({ query });
      const favoritePackageIds = (data?.favoritePackageIds || []).concat(
        packageId
      );
      cache.writeQuery({
        query,
        data: { favoritePackageIds },
      });
    },
  });
}

export function useRemoveFavoritePackage(packageId: string) {
  return useMutation(REMOVE_FAVORITE_PACKAGE, {
    variables: {
      input: { packageId },
    },
    optimisticResponse: {
      __typename: "Mutation",
      RemoveFavoritePackage: {
        __typename: "RemoveFavoritePackagePayload",
      },
    },
    update(cache) {
      const query = FAVORITE_PACKAGE_IDS_QUERY;
      const data = cache.readQuery<{ favoritePackageIds: string[] }>({ query });
      const favoritePackageIds = (data?.favoritePackageIds || []).filter(
        (favoritePackageId) => favoritePackageId !== packageId
      );
      cache.writeQuery({
        query,
        data: { favoritePackageIds },
      });
    },
  });
}

export function useFavoritePackageIdsQuery() {
  const {
    accountInfo: { id: accountId },
  } = useAuthState();
  const lastAccountId = usePrevious(accountId);
  const { data, loading, error } = useQuery(FAVORITE_PACKAGE_IDS_QUERY, {
    ssr: false,
    fetchPolicy: accountId !== lastAccountId ? "network-only" : "cache-first",
  });

  let favoritePackageIds = [];
  if (!error && data?.favoritePackageIds) {
    favoritePackageIds = data.favoritePackageIds;
  }

  return { favoritePackageIds, loading };
}
